import { useState, useCallback } from 'react';
import { api } from '../lib/api-client';
import { getSpineManager } from '../lib/spine-manager';
import type { Asset } from '@spine-workspace/shared';

interface SpineLoadState {
  loading: boolean;
  error: string | null;
}

export function useSpineLoader() {
  const [states, setStates] = useState<Record<string, SpineLoadState>>({});

  const loadAsset = useCallback(async (assetId: string): Promise<boolean> => {
    setStates((s) => ({ ...s, [assetId]: { loading: true, error: null } }));
    try {
      const { asset } = await api.get<{ asset: Asset }>(`/api/assets/${assetId}`);
      if (!asset.jsonUrl || !asset.atlasUrl) throw new Error('Missing asset URLs');

      const manager = getSpineManager();
      await manager.loadSpineAsset(assetId, asset.jsonUrl, asset.atlasUrl, asset.pngUrls ?? []);

      setStates((s) => ({ ...s, [assetId]: { loading: false, error: null } }));
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Load failed';
      console.error(`[SpineLoader] Failed to load asset ${assetId}:`, err);
      setStates((s) => ({ ...s, [assetId]: { loading: false, error: msg } }));
      return false;
    }
  }, []);

  const isLoading = (assetId: string) => states[assetId]?.loading ?? false;
  const getError = (assetId: string) => states[assetId]?.error ?? null;

  return { loadAsset, isLoading, getError };
}
